import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/Layout";
import { Button, ErrorNote, Field, inputClass, useCurrentUser } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { dateTime } from "@/lib/format";

/*
 * Notifiche Telegram del tenant: chat, attivazione, tipi di evento, finestra dei
 * duplicati. Il token del bot sta solo nell'env del server e qui non compare.
 */
export function Notifications() {
  const user = useCurrentUser();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["notifications"], queryFn: api.notifications.get, enabled: user.role === "ADMIN" });
  const [chat, setChat] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [events, setEvents] = useState<string[]>([]);
  const [windowMin, setWindowMin] = useState("15");
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!q.data) return;
    setChat(q.data.telegramChatId ?? "");
    setEnabled(q.data.enabled);
    setEvents(q.data.events);
    setWindowMin(String(q.data.dedupeWindowMinutes));
  }, [q.data]);

  if (user.role !== "ADMIN") {
    return (
      <>
        <PageHeader title="Notifiche" />
        <p className="px-6 py-6 text-sm text-muted">Le notifiche del tenant le configura un amministratore.</p>
      </>
    );
  }

  const save = async () => {
    setError(null);
    setNote(null);
    setBusy(true);
    try {
      const saved = await api.notifications.save({ telegramChatId: chat.trim() || null, enabled, events, dedupeWindowMinutes: Number(windowMin) });
      qc.setQueryData(["notifications"], saved);
      setNote("Salvato.");
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const sendTest = async () => {
    setError(null);
    setNote(null);
    try {
      await api.notifications.test();
      setNote("Messaggio di prova inviato: controlla la chat.");
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const d = q.data;
  return (
    <>
      <PageHeader title="Notifiche" subtitle="Eventi di rischio su Telegram, con il bot già in uso sul primo gestionale" />
      <div className="max-w-2xl space-y-5 px-6 py-5">
        {q.error && <ErrorNote>{errorMessage(q.error)}</ErrorNote>}
        {d && !d.botConfigured && (
          <p className="rounded border border-warn/40 bg-warn/10 px-3 py-2 text-sm text-warn">
            Il bot non è configurato sul server (TELEGRAM_BOT_TOKEN). Gli eventi restano in coda nel database e partono quando c'è.
          </p>
        )}

        <Field label="Id della chat Telegram" hint="Numerico, negativo per i gruppi (es. -1001234567890). Il bot deve essere nel gruppo.">
          {(id) => <input id={id} value={chat} onChange={(e) => setChat(e.target.value)} className={inputClass} inputMode="numeric" placeholder="-100…" />}
        </Field>

        <label className="flex items-center gap-2 text-sm text-ink">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Notifiche attive
        </label>
        {d?.enabledAt && <p className="-mt-3 text-xs text-faint">Attive dal {dateTime(d.enabledAt)}: gli eventi precedenti non si inviano.</p>}

        <fieldset>
          <legend className="mb-1.5 text-sm text-muted">Eventi</legend>
          <div className="space-y-1">
            {d?.kinds
              .filter((k) => k.generated)
              .map((k) => (
                <label key={k.kind} className="flex items-center gap-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    checked={events.includes(k.kind)}
                    onChange={(e) => setEvents((cur) => (e.target.checked ? [...cur, k.kind] : cur.filter((x) => x !== k.kind)))}
                  />
                  {k.label}
                  {k.kind === "NAKED_LEG_TIMEOUT" && <span className="text-xs text-faint">· mai soppressa come duplicato</span>}
                  {(k.kind === "ACCOUNT_DISCONNECTED" || k.kind === "PROXY_DOWN") && <span className="text-xs text-faint">· anche il rientro</span>}
                </label>
              ))}
          </div>
        </fieldset>

        <Field label="Finestra dei duplicati (minuti)" hint="Lo stesso problema ripetuto entro la finestra non riparte. La gamba scoperta parte sempre.">
          {(id) => <input id={id} value={windowMin} onChange={(e) => setWindowMin(e.target.value)} className={`${inputClass} w-32`} inputMode="numeric" />}
        </Field>

        {error && <ErrorNote>{error}</ErrorNote>}
        {note && <p className="text-sm text-muted" role="status">{note}</p>}
        <div className="flex gap-2">
          <Button variant="primary" onClick={() => void save()} disabled={busy || !d}>
            Salva
          </Button>
          <Button onClick={() => void sendTest()} disabled={!d?.telegramChatId || !d.botConfigured}>
            Invia prova
          </Button>
        </div>
      </div>
    </>
  );
}
